/**
 * Customer Portal (UI) - aggregate dashboard route.
 *
 * The route renders a Tabler-style dashboard home page with integrated scope
 * controls and account-scoped data cards.
 */
import { useEffect, useMemo, useState } from 'react';
import { DashboardRouter } from '../components/dashboard/DashboardRouter';
import {
  DeliveryTrendChartCard,
  SmsMmsVolumeLineChartCard,
  StackedVolumeChartCard
} from '../components/dashboard/DashboardCharts';
import { ScopeControls } from '../components/layout/ScopeControls';
import { fetchBrands, fetchCampaigns, fetchDashboard, fetchPhoneNumbers } from '../features/api/client';
import type { AccountContextState } from '../features/account-context/account-context-store';
import type { FilterStore } from '../features/filters/filter-store';
import type { Brand, Campaign, DashboardResponse, PhoneNumber } from '../types/portal';
import { dateInputToIsoBoundary, getBrowserTimeZone, toLocalDateInputValue } from '../utils/datetime';

type DashboardRouteProps = {
  accountContext: AccountContextState;
  filters: FilterStore;
};

export function DashboardRoute({ accountContext, filters }: DashboardRouteProps) {
  const browserTimeZone = useMemo(() => getBrowserTimeZone(), []);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>(() => {
    const start = new Date();
    start.setDate(start.getDate() - 29);
    return toLocalDateInputValue(start);
  });
  const [endDate, setEndDate] = useState<string>(() => toLocalDateInputValue(new Date()));

  const range = useMemo(
    () => ({
      startIso: dateInputToIsoBoundary(startDate, 'start'),
      endIso: dateInputToIsoBoundary(endDate, 'end'),
      timeZone: browserTimeZone
    }),
    [browserTimeZone, endDate, startDate]
  );

  useEffect(() => {
    const accountId = accountContext.selectedAccountId;
    if (!accountId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetchBrands(accountId),
      fetchCampaigns(accountId),
      fetchPhoneNumbers(accountId, filters.filters),
      fetchDashboard(accountId, filters.filters, range)
    ])
      .then(([brandPayload, campaignPayload, phonePayload, dashboardPayload]) => {
        if (cancelled) return;
        setBrands(brandPayload.items);
        setCampaigns(campaignPayload.items);
        setPhoneNumbers(phonePayload.items);
        setDashboard(dashboardPayload.dashboard);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountContext.selectedAccountId, filters.filters, range]);

  if (accountContext.loading) {
    return <div className="alert alert-info">Loading account context...</div>;
  }

  if (accountContext.error) {
    return <div className="alert alert-danger">{accountContext.error}</div>;
  }

  if (!accountContext.selectedAccountId) {
    return <div className="alert alert-warning">No account selected. Choose an account to continue.</div>;
  }

  const chartSeries = dashboard?.series ?? [];
  const hasDashboardShape = Boolean(
    dashboard &&
      dashboard.range &&
      Array.isArray(dashboard.metrics) &&
      Array.isArray(dashboard.series)
  );
  const scopedCampaigns = filters.filters.brandId
    ? campaigns.filter((campaign) => campaign.brandId === filters.filters.brandId)
    : campaigns;
  const volumePoints = chartSeries.map((point) => ({
    hourUtc: point.hourUtc,
    smsOutbound: point.smsOutbound,
    smsInbound: point.smsInbound,
    mmsOutbound: point.mmsOutbound,
    mmsInbound: point.mmsInbound
  }));
  const deliveryPoints = chartSeries.map((point) => ({
    hourUtc: point.hourUtc,
    delivered: point.delivered,
    failed: point.failed,
    received: point.received
  }));
  const smsMmsLinePoints = chartSeries.map((point) => ({
    hourUtc: point.hourUtc,
    smsTotal: point.smsInbound + point.smsOutbound,
    mmsTotal: point.mmsInbound + point.mmsOutbound
  }));

  return (
    <>
      <div className="page-pretitle">Overview</div>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h2 className="page-title mb-0">Dashboard</h2>
        <div className="d-flex flex-wrap align-items-end gap-2">
          <label className="form-label mb-0">
            Start
            <input className="form-control form-control-sm" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label className="form-label mb-0">
            End
            <input className="form-control form-control-sm" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
          <div className="text-secondary small pb-1">
            {dashboard ? `Timezone: ${dashboard.range?.timeZone ?? 'UTC'}` : `Timezone: ${browserTimeZone}`}
            {dashboard?.rollupStatus === 'catching_up' ? ' (rollup catch-up active)' : ''}
          </div>
        </div>
      </div>

      <ScopeControls
        accounts={accountContext.accounts}
        selectedAccountId={accountContext.selectedAccountId}
        canSwitchAccounts={accountContext.canSwitchAccounts}
        brands={brands}
        campaigns={scopedCampaigns}
        phoneNumbers={phoneNumbers}
        filters={filters.filters}
        onSelectAccount={(accountId) => {
          accountContext.setSelectedAccountId(accountId);
          filters.resetFilters();
        }}
        onSelectBrand={(brandId) => {
          filters.setBrandId(brandId);
        }}
        onSelectCampaign={(campaignId) => {
          filters.setCampaignId(campaignId);
          filters.setPhoneNumberId(null);
        }}
        onSelectPhoneNumber={(phoneNumberId) => filters.setPhoneNumberId(phoneNumberId)}
        onResetFilters={filters.resetFilters}
      />

      {loading ? <div className="alert alert-info">Loading dashboard data...</div> : null}
      {error ? <div className="alert alert-danger">{error}</div> : null}

      {!loading && !error && dashboard && hasDashboardShape ? (
        <>
          <div className="row row-cards mb-3">
            <div className="col-12">
              <DashboardRouter dashboard={dashboard} />
            </div>
          </div>
          <div className="row row-cards">
            <div className="col-12">
              <SmsMmsVolumeLineChartCard points={smsMmsLinePoints} />
            </div>
            <div className="col-xl-8">
              <StackedVolumeChartCard points={volumePoints} />
            </div>
            <div className="col-xl-4">
              <DeliveryTrendChartCard points={deliveryPoints} />
            </div>
          </div>
        </>
      ) : null}
      {!loading && !error && dashboard && !hasDashboardShape ? (
        <div className="alert alert-warning">
          Dashboard response is missing required fields. Refresh the page or contact support if this persists.
        </div>
      ) : null}
      {!loading && !error && !dashboard ? (
        <div className="alert alert-secondary">
          No dashboard data is available for the selected account and date range.
        </div>
      ) : null}
    </>
  );
}
